import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SectionedDialog from './SectionedDialog';
import AccessSection from './AccessSection';
import FunctionSetAccess from './FunctionSetAccess';
import DisplayCredentialSection from './DisplayCredentialSection';
import PropertiesSection from './PropertiesSection';
import SettingsSection, { declaredLegacyKeys, fetchSettingsSchema, settingsPatch } from './SettingsSection';

// admin-node-station-edit slug: station.config keys OWNED by a dedicated
// section above — the Properties section shows only the REMAINING raw keys, so
// we never double-edit a value the Conversation/Output/Aggregator/Helpers
// sections already manage (the user's decision: "only keys not owned by other
// sections"). Greeting is stored off-config (its own endpoint) but listed here
// defensively in case a legacy row carried a `config.greeting`.
const OWNED_CONFIG_KEYS = [
  'cardinality', 'participant', 'transactional_provider', 'resolver_mode', 'resolver',
  'output_observers_max', 'output_share_ttl_hours', 'output_share_public', 'embed_public', 'embed_act',
  'secondary_display', 'presto_enabled',   // presto_enabled = legacy alias (owned → hidden from Properties)
  'backup',   // node-data-backup: owned by the Backup section's toggle
  'chat_file_retention_days',   // chat-file-retention: owned by the Backup section
  'display_source',   // presto-scheduled-view: live | scheduled (owned by the Display section)
  'image_url',   // station-avatar: the station picture (owned by the Image section)
  'llm', 'aggregator', 'helpers', 'greeting',
  'settings',   // station-settings-admin: helper-declared settings, owned by the Settings section
  // admin-display-provisioning: the display's frame-fetch credential + its
  // management metadata are owned by the Display section — never surface the
  // plaintext `api_key` (a device-grade secret, ADR D4) in the raw Properties editor.
  'api_key', 'display_account_id', 'display_read_key_id', 'display_key_prefix',
  'display_provisioned_at',
  // dead per-helper flags the hub strips on save — never offer them for edit.
  'sensor_enabled', 'hiring_service_enabled', 'tobin_suika_enabled', 'job_listing_enabled',
];

// station-private-log-redaction: `config._private` holds operator-set vendor credentials.
// The server already sends only its key NAMES with masked values; listing it here as masked
// makes the Properties row read-only and keeps it out of every save patch, so the mask can
// never be written back over the stored secret (the hub shallow-merges station config).
const MASKED_CONFIG_KEYS = ['_private'];
// Stable (module-level) so PropertiesSection's memos don't rebuild on every render.
const NO_CRITICAL_KEYS = [];

/** Modal form for creating or editing a station.
 *  - Create mode (initial=null): name + address + optional lat/lng. Posts
 *    to /api/stations/ with the host node_id.
 *  - Edit mode (initial set): same form, prepopulated. PATCHes to
 *    /api/stations/{id} with only the changed fields.
 *
 *  Helpers fieldset (station-helper-gating slug): checkboxes for each
 *  helper the parent node can publish. Default = all checked, which
 *  omits `config.helpers.*` so the runtime gate falls through to "all
 *  node helpers." Saving any subset writes `config.helpers.included`
 *  with the checked set. The hub strips dead per-helper `<name>_enabled`
 *  flags from station.config on every save that touches config (lazy
 *  backfill).
 *
 *  Picker source: `/api/providers` (in-process aveq-admin catalog) ∩
 *  the node's `helpers_included` stamp (from /api/nodes/{id}/helpers-
 *  included). No NodeService call. If the node has no stamp, the full
 *  catalog is the source.
 *
 *  Status flip and hard delete have their own affordances on the station
 *  row in NodeDetail (Disable/Enable button, Delete with typed-confirm)
 *  and don't go through this form.
 */
// Dead per-helper `<name>_enabled` flags the hub strips on any save
// that touches station.config. Surfaced here so the UI can flag them
// in a one-time hint when an operator opens an existing station that
// still carries them. NOT including `tobin_enabled` — it's the live
// Tobin-restaurant marker, not a helper toggle. Mirror of the hub's
// _DEAD_ENABLED_FLAGS in aveq-hub/web_entry.py.
// The helper that broadcasts a shared room's messages (@Helper(broadcasting="say")).
const ROOM_HELPER = 'station_chat_endpoints';

const DEAD_ENABLED_FLAGS = [
  'sensor_enabled', 'hiring_service_enabled',
  'tobin_suika_enabled', 'job_listing_enabled',
];

// broadcasting-as-shared-session (D9): the conversation config is two
// orthogonal axes, both fetched at mount from `GET /api/modes` (proxied by the
// admin BFF to the agent) — the single source of truth (the agent's
// variants/registry catalog):
//   - cardinality (session topology): private = 1-1 per visitor; shared = one
//     room per station (needs the station_chat_endpoints helper).
//   - participant (responder kind/backend): conversational=Sonnet,
//     limited_conversational=Haiku, transactional=function-only (pick a
//     provider), command=strict parser.
// The leading "— default —" option in each picker is the only fixed entry (it
// represents "unset"; the agent defaults to private / conversational). See
// `cardinalityOptions` / `participantOptions` / `transactionalProviders` below.

// Legacy resolver value → new participant strategy (for the transition window
// during which a station might still carry the v0.96.0 single-key shape).
// Mirror of aveq-agent/variants/registry.py:_ALIASES.
const RESOLVER_LEGACY_MAP = {
  'claude':  'conversational',
  'haiku':   'limited_conversational',
  'lite':    'transactional',
  'command': 'command',
};

// Legacy resolver "lite" → which transactional provider to pre-select
// when reading a not-yet-migrated station. Mirrors the agent's
// `_DEFAULT_TRANSACTIONAL_PROVIDER` in variants/lite.py:init().
const LEGACY_LITE_DEFAULT_PROVIDER = 'together-llama-3.3-70b';

export default function StationFormModal({ nodeId, initial, onClose, onSaved, networkCanonical, onAccessChanged }) {
  const editing = !!initial;
  // admin-node-station-edit slug: the Properties section reports its accumulated
  // change set (key → newValue, or key → null for removals) here; merged into
  // the station config jsonb on save. Empty until the operator edits a property.
  const [propsPatch, setPropsPatch] = useState({});
  // station-settings-admin: edits to helper-declared settings, by "<Helper>.<key>".
  const [settingsEdits, setSettingsEdits] = useState({});
  const [settingsSchema, setSettingsSchema] = useState(null);
  useEffect(() => {
    if (!editing || !initial?.id) return undefined;
    let cancelled = false;
    fetchSettingsSchema(initial.id)
      .then((s) => { if (!cancelled) setSettingsSchema(s); })
      .catch(() => { if (!cancelled) setSettingsSchema('failed'); });
    return () => { cancelled = true; };
  }, [editing, initial?.id]);
  const propertiesOwnedKeys = useMemo(
    () => [...OWNED_CONFIG_KEYS, ...declaredLegacyKeys(settingsSchema)], [settingsSchema]);
  const handlePropsPatch = useCallback((patch) => setPropsPatch(patch || {}), []);
  const initialGreeting = initial?.greeting || '';
  // station-empty-greeting: config.greeting === '' (present-but-empty) means the
  // operator chose "No greeting" (vs. absent → LLM auto-greeting). Prefill the
  // checkbox from that exact sentinel.
  const initialGreetingOff = initial?.greeting === '';
  const [name, setName] = useState(initial?.name || '');
  const [address, setAddress] = useState(initial?.address || '');
  // station-description: short blurb shown under the station name on /s/<id>.
  const [description, setDescription] = useState(initial?.description || '');
  // station-ownership-and-audit: chat-user owner = aveq-agent account.id. Raw id
  // field (no account picker yet — no account-list endpoint; a name-resolving
  // picker is a follow-up). '' = unassigned. View + set; enforcement deferred.
  const initialOwner = initial?.owner_account_id != null ? String(initial.owner_account_id) : '';
  const [owner, setOwner] = useState(initialOwner);
  const [lat, setLat] = useState(initial?.lat != null ? String(initial.lat) : '');
  const [lng, setLng] = useState(initial?.lng != null ? String(initial.lng) : '');
  const [greeting, setGreeting] = useState(initialGreeting);
  const [greetingOff, setGreetingOff] = useState(initialGreetingOff);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // admin-sectioned-dialogs: which category's detail pane is showing.
  const [activeCat, setActiveCat] = useState('basics');

  // broadcasting-as-shared-session (D9): `resolver_mode` decomposed into two
  // orthogonal axes the form now sets independently —
  //   - cardinality: session topology (writes config.cardinality):
  //       private = a 1-1 session per user; shared = one room per station.
  //   - participant: responder kind/backend (writes config.participant):
  //       conversational / limited_conversational / transactional / command.
  // + optional transactional_provider (when participant = transactional).
  // Empty = "fall through to the agent default" (private / conversational).
  //
  // Initial values prefer the new keys, then derive from the transition-window
  // legacy `resolver_mode` (broadcasting → {shared, conversational}; every other
  // mode → {private, <mode>}) and the even-older single-key `resolver`.
  const legacyMode = (
    initial?.config?.resolver_mode
    || RESOLVER_LEGACY_MAP[initial?.config?.resolver]
    || ''
  );
  const initialCardinality = (
    initial?.config?.cardinality
    || (legacyMode === 'broadcasting' ? 'shared' : (legacyMode ? 'private' : ''))
  );
  const initialParticipant = (
    initial?.config?.participant
    || (legacyMode === 'broadcasting' ? 'conversational' : (legacyMode || ''))
  );
  const initialProvider = (
    initial?.config?.transactional_provider
    || (initial?.config?.resolver === 'lite' ? LEGACY_LITE_DEFAULT_PROVIDER : '')
  );
  const [cardinality, setCardinality] = useState(initialCardinality);
  const [participant, setParticipant] = useState(initialParticipant);
  const [transactionalProvider, setTransactionalProvider] = useState(initialProvider);

  // station-chat-web-push: per-station web-push opt-in. Only meaningful for a
  // shared room (cardinality='shared') — the checkbox is hidden otherwise and the
  // value is force-cleared on save when cardinality isn't shared.
  const initialWebPush = !!initial?.config?.web_push;
  const [webPush, setWebPush] = useState(initialWebPush);

  // station-avatar: the station picture, stored as a small data: URI in
  // config.image_url. New uploads are downscaled client-side to a small square
  // (no backend upload endpoint) before they enter this state.
  const initialImage = initial?.config?.image_url || '';
  const [imageDataUri, setImageDataUri] = useState(initialImage);
  const [imageError, setImageError] = useState(null);
  // station-avatar-save-undo: the Image section saves on its own (a PATCH
  // carrying only config.image_url) so an operator can swap a picture without
  // committing whatever is half-edited in the other panes. `savedImage` is the
  // last PERSISTED picture — it is the baseline both for Undo and for the dirty
  // check in submit(), so a section save isn't pointlessly re-sent when the
  // operator later hits the footer Save.
  const [savedImage, setSavedImage] = useState(initialImage);
  const [imageSaving, setImageSaving] = useState(false);
  const [imageReverting, setImageReverting] = useState(false);
  const [imageMsg, setImageMsg] = useState(null);
  // station-avatar-generate: which painted variant the Generate button will ask
  // for next. The generator is deterministic on station id, so variant 0 is the
  // ONE canonical picture for this station (exactly what the fleet seeding
  // produced); without a variant, clicking Generate twice would return the same
  // image and read as a broken button. Each click hands back the next variant.
  const [imageGenVariant, setImageGenVariant] = useState(0);
  const [imageGenerating, setImageGenerating] = useState(false);

  // aggregator-station-llm-config slug: per-aggregator-station Claude model +
  // API key (writes config.llm.{model, api_key_enc}). Only shown for aggregator
  // stations (config.aggregator.module set). The key is write-only — we never
  // receive the plaintext back; the modal only knows whether one is set
  // (config.llm.api_key_enc present). Entering a value replaces it; the Clear
  // checkbox removes it. The admin BFF encrypts the plaintext before the hub.
  const isAggregatorStation = !!initial?.config?.aggregator?.module;
  const initialLlmModel = initial?.config?.llm?.model || '';
  const llmKeyIsSet = !!initial?.config?.llm?.api_key_enc;
  const [llmModel, setLlmModel] = useState(initialLlmModel);
  const [llmApiKey, setLlmApiKey] = useState('');      // new plaintext, if entered
  const [clearLlmKey, setClearLlmKey] = useState(false);
  // workflow-station-props: per-run token budget (config.llm.token_budget) — the LLM
  // spend cap the workflow stops at. Blank = the server default (WORKFLOW_TOKEN_BUDGET).
  const initialLlmBudget = initial?.config?.llm?.token_budget != null
    ? String(initial.config.llm.token_budget) : '';
  const [llmBudget, setLlmBudget] = useState(initialLlmBudget);
  // resolver-max-tokens: the agent's function-resolver output cap
  // (config.llm.resolver_max_tokens) — beside the model and the key, because the
  // right value depends on this station's helpers (a free-text `goal` needs far more
  // than `resolve_station(name)`). Blank = the agent default.
  const initialLlmResolverCap = initial?.config?.llm?.resolver_max_tokens != null
    ? String(initial.config.llm.resolver_max_tokens) : '';
  const [llmResolverCap, setLlmResolverCap] = useState(initialLlmResolverCap);

  // workflow-generic: per-Workflow-station settings (config.workflow). Deployment
  // policy for the orchestrator this station runs — house routing rules, what it
  // must not drive, how long it waits per call, how much trace it keeps. Only read
  // by a station running the Workflow module; harmless elsewhere. Kept as strings;
  // '' = "server default" → the key is sent as null so the hub's jsonb merge clears it.
  const initialWf = initial?.config?.workflow || {};
  const initialWfGuidance = initialWf.guidance || '';
  const initialWfExclude = Array.isArray(initialWf.exclude) ? initialWf.exclude.join('\n') : '';
  const initialWfTimeout = initialWf.dispatch_timeout_s != null ? String(initialWf.dispatch_timeout_s) : '';
  const initialWfKeep = initialWf.keep_recent_turns != null ? String(initialWf.keep_recent_turns) : '';
  const [wfGuidance, setWfGuidance] = useState(initialWfGuidance);
  const [wfExclude, setWfExclude] = useState(initialWfExclude);
  const [wfTimeout, setWfTimeout] = useState(initialWfTimeout);
  const [wfKeep, setWfKeep] = useState(initialWfKeep);

  // output-second-screen slug: per-station OUTPUT (card) panel controls.
  //  - output_observers_max: 0 = chat-only (no output panel); 1 (default) =
  //    panel + 1 shareable second screen; N = N second screens.
  //  - output_share_ttl_hours: lifetime of a /s/<id>/<token> share link.
  // Kept as input strings; '' = "leave at default" → key omitted on create,
  // sent as null on edit-clear so the hub's jsonb shallow-merge falls through.
  const initialObsMax = initial?.config?.output_observers_max;
  const initialShareTtl = initial?.config?.output_share_ttl_hours;
  // chat-file-retention: days chat files are kept; '' = keep forever.
  const initialRetention = initial?.config?.chat_file_retention_days;
  const [fileRetentionDays, setFileRetentionDays] = useState(
    initialRetention == null ? '' : String(initialRetention));
  const [outputObserversMax, setOutputObserversMax] = useState(
    initialObsMax == null ? '' : String(initialObsMax));
  const [outputShareTtlHours, setOutputShareTtlHours] = useState(
    initialShareTtl == null ? '' : String(initialShareTtl));
  // output-share-public slug: opt-in anonymous (logged-out) viewing of the
  // station's output share link (kiosk / wall display). Default off.
  const initialSharePublic = !!initial?.config?.output_share_public;
  const [outputSharePublic, setOutputSharePublic] = useState(initialSharePublic);
  // embeddable-card: opt-in to ANONYMOUS, token-less embedding of this station's
  // read-only card output on a third-party page (/embed/s/<id>). Default off.
  const initialEmbedPublic = !!initial?.config?.embed_public;
  const [embedPublic, setEmbedPublic] = useState(initialEmbedPublic);
  // embeddable-card-interactive: 2nd opt-in — allow anonymous ACTIONS from an embed.
  const initialEmbedAct = !!initial?.config?.embed_act;
  const [embedAct, setEmbedAct] = useState(initialEmbedAct);
  // presto-display-config: opt this station into the secondary-display (split-output) second
  // screen. Renamed from presto_enabled (still read as a legacy alias for existing opt-ins).
  const initialSecondaryDisplay = !!(initial?.config?.secondary_display
    ?? initial?.config?.presto_enabled);
  const [secondaryDisplay, setSecondaryDisplay] = useState(initialSecondaryDisplay);
  // presto-scheduled-view: what a secondary display (e.g. a Presto) shows — the station's live
  // interactive output ('live', default) or its latest scheduled-task cards ('scheduled').
  const initialDisplaySource = initial?.config?.display_source === 'scheduled' ? 'scheduled' : 'live';
  const [displaySource, setDisplaySource] = useState(initialDisplaySource);
  // node-data-backup: opt this station's durable files into the off-host backup.
  const initialBackup = !!initial?.config?.backup;
  const [backup, setBackup] = useState(initialBackup);

  // broadcasting-as-shared-session (D9): the cardinality + participant option
  // lists come from the agent's catalog via GET /api/modes (proxied by the admin
  // BFF), the single source of truth. The slug decomposed the old flat `modes`
  // list into `{ cardinalities, participants }`. `null` = still loading; `{}` =
  // agent unreachable (we degrade to just the "— default —" choices rather than
  // re-introducing hardcoded lists).
  const [modesCatalog, setModesCatalog] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/modes');
        const data = res.ok ? await res.json() : { cardinalities: [], participants: [] };
        if (!cancelled) setModesCatalog(data || {});
      } catch (_e) {
        if (!cancelled) setModesCatalog({});
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // lite-node-kind: a display device announces `kind: "display"` in its published
  // spec (aveq-display via lite_node.set_kind). Read it so the Display credential
  // section shows ONLY for actual displays — not every editable station, and not
  // sensor Picos (which announce "sensor"). null = loading; '' / other = not a
  // display (incl. legacy devices that haven't republished a kind → section hidden).
  const [stationKind, setStationKind] = useState(null);
  useEffect(() => {
    if (!editing || !initial?.id) { setStationKind(''); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/stations/${encodeURIComponent(initial.id)}/spec`,
                                { credentials: 'same-origin' });
        if (!res.ok) { if (!cancelled) setStationKind(''); return; }
        const data = await res.json();
        const kind = (data && data.entity && data.entity.spec
                      && data.entity.spec.station && data.entity.spec.station.kind) || '';
        if (!cancelled) setStationKind(kind);
      } catch (_e) {
        if (!cancelled) setStationKind('');
      }
    })();
    return () => { cancelled = true; };
  }, [editing, initial]);

  // Cardinality options: the catalog's topology choices. No leading "default"
  // option (add-station-conv-parity: Add now matches Edit) — an unset value falls
  // back to the Private radio (see the renderConversation default).
  const cardinalityOptions = useMemo(() => {
    const cards = (modesCatalog && modesCatalog.cardinalities) || [];
    return cards.map((c) => ({ value: c.value, label: c.label }));
  }, [modesCatalog]);

  // Participant (responder) options: one per participant the agent publishes. No
  // leading "default" (Add matches Edit) — unset falls back to the Conversational radio.
  const participantOptions = useMemo(() => {
    const parts = (modesCatalog && modesCatalog.participants) || [];
    return parts.map((p) => ({ value: p.name, label: p.label }));
  }, [modesCatalog]);

  // Transactional provider options come from the `transactional` participant's
  // sub_options. Empty until the fetch lands (or if the agent is unreachable).
  const transactionalProviders = useMemo(() => {
    const parts = (modesCatalog && modesCatalog.participants) || [];
    const tx = parts.find((p) => p.name === 'transactional');
    return tx && tx.sub_options
      ? tx.sub_options.map((o) => ({ value: o.id, label: o.label }))
      : [];
  }, [modesCatalog]);

  // station-helper-gating slug — Helpers picker state.
  // catalogModules: every module aveq-admin's PROVIDER_MAP knows about
  // (system-wide catalog from /api/providers).
  // nodeStampedHelpers: the parent node's helpers_included list (parsed
  // from /api/nodes/{id}/helpers-included). Empty when not stamped
  // (node loads everything) OR when bare-relay (distinguished by
  // nodeIsBareRelay).
  const [catalogModules, setCatalogModules] = useState(null);
  const [nodeStampedHelpers, setNodeStampedHelpers] = useState(null);
  // admin-helper-picker-node-gate: the node's helpers_excluded list (when the
  // node uses an exclude policy instead of include); candidate = catalog − this.
  const [nodeExcludedHelpers, setNodeExcludedHelpers] = useState([]);
  const [nodeIsBareRelay, setNodeIsBareRelay] = useState(false);
  // station-picker-node-local-helpers slug: modules the node actually LOADED but
  // that aren't in the provider catalog (custom helpers authored in a standalone
  // node's extra/). Sourced from /api/nodes/{id}/loaded-helpers (node probe);
  // surfaced as pickable "node-local" candidates. Empty when the node can't be
  // probed → picker degrades to catalog-only.
  const [nodeLocalHelpers, setNodeLocalHelpers] = useState([]);
  const [selectedHelpers, setSelectedHelpers] = useState(() => new Set());
  const [helpersInitialized, setHelpersInitialized] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // station-picker-node-local-helpers: third fetch surfaces helpers the
        // node loaded that aren't in the catalog (custom extra/ helpers). It's
        // best-effort — the endpoint returns {loaded:[],available:false} when the
        // node can't be probed, so the picker degrades to catalog-only.
        const loadedUrl = networkCanonical
          ? `/api/nodes/${encodeURIComponent(nodeId)}/loaded-helpers?network=${encodeURIComponent(networkCanonical)}`
          : `/api/nodes/${encodeURIComponent(nodeId)}/loaded-helpers`;
        const [catRes, nodeRes, loadedRes] = await Promise.all([
          fetch('/api/providers'),
          fetch(`/api/nodes/${encodeURIComponent(nodeId)}/helpers-included`),
          fetch(loadedUrl),
        ]);
        const cat = catRes.ok ? await catRes.json() : { providers: [] };
        const node = nodeRes.ok ? await nodeRes.json()
          : { helpers_included: null, helpers_list: [], helpers_excluded_list: [] };
        const loaded = loadedRes.ok ? await loadedRes.json() : { loaded: [] };
        if (cancelled) return;
        const catModules = (cat.providers || []).map((p) => p.module);
        const catSet = new Set(catModules);
        setCatalogModules(catModules);
        setNodeStampedHelpers(node.helpers_list || []);
        setNodeExcludedHelpers(node.helpers_excluded_list || []);
        setNodeIsBareRelay(node.helpers_included === '__none__');
        // node-local = loaded modules the catalog doesn't know about.
        setNodeLocalHelpers((loaded.loaded || []).filter((m) => !catSet.has(m)));
      } catch (_e) {
        if (!cancelled) { setCatalogModules([]); setNodeStampedHelpers([]); setNodeExcludedHelpers([]); setNodeLocalHelpers([]); }
      }
    })();
    return () => { cancelled = true; };
  }, [nodeId, networkCanonical]);

  // Candidate modules = helpers the operator can pick for THIS station — only
  // ones the parent node will actually LOAD (admin-helper-picker-node-gate
  // slug). The node policy is `helpers_included` XOR `helpers_excluded`:
  //   - bare-relay (__none__) ⇒ empty candidate set (fieldset shows the note).
  //   - included stamped ⇒ catalog ∩ included (stamped names absent from the
  //     catalog are dropped).
  //   - excluded stamped ⇒ catalog − excluded.
  //   - neither ⇒ node loads everything ⇒ full catalog.
  // Pre-slug this only read `helpers_included` via the standalone-credentials
  // path, which 412'd for in-stack nodes → fell open to the full catalog and
  // offered helpers the node couldn't publish.
  const candidateModules = useMemo(() => {
    if (catalogModules === null || nodeStampedHelpers === null) return null;
    const cat = new Set(catalogModules);
    // Catalog-bounded base set (node policy applied), per the branches above.
    let base;
    if (nodeIsBareRelay) {
      base = [];
    } else if (nodeStampedHelpers.length > 0) {
      base = nodeStampedHelpers.filter((m) => cat.has(m));
    } else if (nodeExcludedHelpers && nodeExcludedHelpers.length > 0) {
      const drop = new Set(nodeExcludedHelpers);
      base = catalogModules.filter((m) => !drop.has(m));
    } else {
      base = catalogModules;
    }
    // station-picker-node-local-helpers: union node-local helpers (loaded on the
    // node, not in the catalog). They bypass node policy (extra/), so they're
    // always offerable regardless of the include/exclude branch.
    const seen = new Set(base);
    return [...base, ...nodeLocalHelpers.filter((m) => !seen.has(m))];
  }, [catalogModules, nodeStampedHelpers, nodeExcludedHelpers, nodeIsBareRelay, nodeLocalHelpers]);

  // Initialize the selection once candidates are known.
  // - station.config.helpers.included present → only those checked
  // - station.config.helpers.excluded present → all except those checked
  // - neither (or Add mode) → none checked: qlink publishes no local helper
  //   without an import list, so "all checked" would misstate what runs.
  useEffect(() => {
    if (helpersInitialized || candidateModules === null) return;
    const cfgHelpers = (initial && initial.config && initial.config.helpers) || {};
    if (Array.isArray(cfgHelpers.included)) {
      const cands = new Set(candidateModules);
      setSelectedHelpers(new Set(cfgHelpers.included.filter((m) => cands.has(m))));
    } else if (Array.isArray(cfgHelpers.excluded)) {
      const ex = new Set(cfgHelpers.excluded);
      setSelectedHelpers(new Set(candidateModules.filter((m) => !ex.has(m))));
    } else {
      setSelectedHelpers(new Set(cardinality === 'shared' && candidateModules.includes(ROOM_HELPER)
        ? [ROOM_HELPER] : []));
    }
    setHelpersInitialized(true);
  }, [candidateModules, helpersInitialized, initial, cardinality]);

  // station-chat-broadcast-variant: when the operator turns ON
  // `station_chat_endpoints` AND the conversation mode is still on the
  // env default, nudge them to broadcasting — the only mode that
  // routes plain text through StationChat.say. Inlined into toggleHelper
  // (broadcasting-autopair-fix slug, v0.99.2 → v0.100.1 hotfix) so it
  // fires only on actual operator click. The prior useEffect-based
  // shape watched `selectedHelpers` for changes and tripped on the
  // initial-population render too, mis-flipping every existing
  // station's mode to Broadcasting on open. Stays put on a deliberate
  // non-default mode (no overwrite).
  const toggleHelper = (m) => setSelectedHelpers((prev) => {
    const next = new Set(prev);
    if (next.has(m)) next.delete(m); else next.add(m);
    // broadcasting-as-shared-session: turning ON the room helper while
    // cardinality is still default nudges the station to a shared room — the
    // topology that routes plain text through StationChat.say. (Was: nudge
    // resolver_mode → 'broadcasting' pre-decomposition.)
    if (m === ROOM_HELPER && next.has(m) && cardinality === '') {
      setCardinality('shared');
    }
    return next;
  });

  // A shared room needs the room helper to broadcast; tick it when the node offers it.
  const chooseCardinality = (value) => {
    setCardinality(value);
    if (value === 'shared' && candidateModules && candidateModules.includes(ROOM_HELPER)) {
      setSelectedHelpers((prev) => new Set([...prev, ROOM_HELPER]));
    }
  };

  // Without the room helper a shared room can't broadcast; block the save and say why.
  const roomHelperError = useMemo(() => {
    if (cardinality !== 'shared' || !helpersInitialized || candidateModules === null) return null;
    if (selectedHelpers.has(ROOM_HELPER)) return null;
    return candidateModules.includes(ROOM_HELPER)
      ? `A shared room needs the ${ROOM_HELPER} helper. Tick it under Helpers.`
      : `A shared room needs the ${ROOM_HELPER} helper, and this node doesn't load it `
        + `(node helper policy). Add it to the node's helpers, or pick Private.`;
  }, [cardinality, helpersInitialized, candidateModules, selectedHelpers]);

  // One-time hint: this station carries legacy `*_enabled` flags that
  // the next save will scrub via the hub's strip-list.
  const hasLegacyEnabledFlags = useMemo(() => {
    const cfg = (initial && initial.config) || {};
    return DEAD_ENABLED_FLAGS.some((k) => Object.prototype.hasOwnProperty.call(cfg, k));
  }, [initial]);

  // helper-requirements slug: live conflict check. When EDITING an existing
  // station, debounce-post the draft config to the hub dry-run (A1) and surface
  // any helper-requirement conflicts — HARD ones block Save (via the conversation
  // category's `error`), SOFT ones just warn. A new station (no id / no registered
  // specs yet) gets no live check; the hub save-backstop catches a conflict on the
  // next save once helpers register.
  const requirementsDraft = useMemo(() => {
    const cfg = { cardinality: cardinality || null };
    if (participant) cfg.participant = participant;
    if (helpersInitialized && candidateModules && candidateModules.length > 0) {
      cfg.helpers = { included: candidateModules.filter((m) => selectedHelpers.has(m)) };
    }
    return cfg;
  }, [cardinality, participant, selectedHelpers, helpersInitialized, candidateModules]);

  const [conflicts, setConflicts] = useState([]);
  useEffect(() => {
    if (!editing || !initial?.id || !helpersInitialized) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await fetch(
          `/api/stations/${encodeURIComponent(initial.id)}/requirements-check`,
          { method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ config: requirementsDraft }) });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setConflicts((data && data.entity && data.entity.conflicts) || []);
      } catch (e) { /* non-fatal — the hub save-backstop still enforces */ }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [editing, initial, helpersInitialized, requirementsDraft]);

  const hardConflicts = conflicts.filter((c) => c.severity === 'hard');
  const softConflicts = conflicts.filter((c) => c.severity !== 'hard');

  const renderConflicts = () => {
    if (conflicts.length === 0) return null;
    return (
      <div style={{ marginBottom: 12 }}>
        {hardConflicts.map((c, i) => (
          <div key={`h${i}`} style={conflictHardStyle}>
            ⛔ <strong>{c.helper}</strong> requires {c.key} = {(c.allowed || []).join(' / ')}
            {' '}(currently {String(c.actual)}).{c.reason ? ` ${c.reason}` : ''}
          </div>
        ))}
        {softConflicts.map((c, i) => (
          <div key={`s${i}`} style={conflictSoftStyle}>
            ⚠️ <strong>{c.helper}</strong> prefers {c.key} = {(c.allowed || []).join(' / ')}
            {' '}(currently {String(c.actual)}).{c.reason ? ` ${c.reason}` : ''}
          </div>
        ))}
      </div>
    );
  };

  // Route a station write through the owning node's network qbric so the BFF's
  // post-save reload-stations admin:exec reaches a peer-network node (long note
  // in submit() below). Shared by the full-form submit and the Image section's
  // own save so the two can't drift.
  const stationRouteQs = (u) => {
    const params = [];
    if (editing && nodeId) params.push(`node_id=${encodeURIComponent(nodeId)}`);
    if (networkCanonical) params.push(`network=${encodeURIComponent(networkCanonical)}`);
    if (!params.length) return '';
    return `${u.includes('?') ? '&' : '?'}${params.join('&')}`;
  };

  const submit = async (e) => {
    // Called from SectionedDialog's primary button (no event) and defensively
    // guarded so a stray form-submit event is still handled.
    if (e && e.preventDefault) e.preventDefault();
    if (!name.trim()) { setError('Name is required.'); return; }
    // Setting or shortening retention deletes files within the hour, and they aren't backed up.
    const _newRet = parseInt(fileRetentionDays.trim(), 10);
    if (_newRet >= 1 && (initialRetention == null || _newRet < initialRetention)
        && !window.confirm(`Chat files older than ${_newRet} day${_newRet === 1 ? '' : 's'} `
                           + 'will be permanently deleted within the hour. This can\'t be undone.')) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      let url, method, body;
      if (editing) {
        // PATCH only what's plausibly changed. Address can be cleared
        // by sending "" (the backend coerces to NULL).
        url = `/api/stations/${initial.id}`;
        method = 'PATCH';
        // station-description: sent every PATCH so clearing the field ("") clears
        // the column (the hub COALESCEs '' → cleared, NULL → unchanged).
        body = { name: name.trim(), address: address.trim(), description: description.trim() };
      } else {
        url = '/api/stations/';
        method = 'POST';
        body = { node_id: nodeId, name: name.trim() };
        if (address.trim()) body.address = address.trim();
        // station-description is EDIT-ONLY (the create SP takes no description);
        // set it via Edit Station after the station exists.
        if (lat.trim() && lng.trim()) {
          const latN = parseFloat(lat), lngN = parseFloat(lng);
          if (Number.isNaN(latN) || Number.isNaN(lngN)) {
            setError('lat/lng must be numbers.'); setSubmitting(false); return;
          }
          body.lat = latN; body.lng = lngN;
        }
      }
      // Always send the import list: with none, qlink publishes no local helper.
      // Any save that includes config also strips dead `<name>_enabled` flags hub-side.
      if (helpersInitialized && candidateModules && candidateModules.length > 0) {
        const included = candidateModules.filter((m) => selectedHelpers.has(m));
        body.config = { helpers: { included } };
      }
      // broadcasting-as-shared-session (D9): merge the two decomposed axes +
      // transactional_provider. Each key is sent only when changed — Create with
      // empty stays "use default" (omitted). Edit clearing a prior value sends
      // the key as `null` so the hub's jsonb shallow-merge sets JSON null, which
      // the agent's `.get(...) or default` chain treats as falsy. Spread
      // preserves any helpers patch above.
      if (cardinality !== initialCardinality) {
        body.config = {
          ...(body.config || {}),
          cardinality: cardinality || null,
        };
      }
      // station-chat-web-push: send web_push when its EFFECTIVE value changed.
      // Force-clear (false) when cardinality isn't shared. NOTE: the hub does
      // NOT validate web_push — unknown config keys pass through its shallow
      // jsonb merge untouched — so this client-side force-clear is the ONLY
      // guard keeping a stale `web_push: true` from riding along after a room
      // flips away from shared.
      const effectiveWebPush = cardinality === 'shared' ? webPush : false;
      if (effectiveWebPush !== initialWebPush) {
        body.config = { ...(body.config || {}), web_push: effectiveWebPush };
      }
      // station-avatar: send image_url when changed. Removed → null so the hub's
      // shallow jsonb merge clears it; the SPA treats null/absent as "no pic".
      // Compared against savedImage, not initialImage — the Image section can
      // have already persisted a picture (station-avatar-save-undo).
      if (imageDataUri !== savedImage) {
        body.config = { ...(body.config || {}), image_url: imageDataUri || null };
      }
      if (participant !== initialParticipant) {
        body.config = {
          ...(body.config || {}),
          participant: participant || null,
          // When the participant flips away from transactional, clear any stale
          // transactional_provider — the hub validator rejects a non-null
          // provider on a non-transactional participant.
          ...(participant !== 'transactional'
              ? { transactional_provider: null }
              : {}),
        };
      }
      if (participant === 'transactional'
          && transactionalProvider !== initialProvider) {
        body.config = {
          ...(body.config || {}),
          transactional_provider: transactionalProvider || null,
        };
      }
      // Clear the legacy single-key shape whenever we touch conversation bits, so
      // a mid-transition operator save migrates a not-yet-migrated row to the new
      // decomposed shape (drops resolver_mode + the even-older resolver).
      if (cardinality !== initialCardinality
          || participant !== initialParticipant
          || transactionalProvider !== initialProvider) {
        if (initial?.config?.resolver_mode) {
          body.config = { ...(body.config || {}), resolver_mode: null };
        }
        if (initial?.config?.resolver) {
          body.config = { ...(body.config || {}), resolver: null };
        }
      }
      // aggregator-station-llm-config: merge config.llm.{model, api_key_plain}
      // only for aggregator stations and only when something changed. The BFF
      // encrypts api_key_plain -> api_key_enc; null clears the stored key.
      if (isAggregatorStation) {
        const llmPatch = {};
        if (llmModel !== initialLlmModel) llmPatch.model = llmModel.trim() || null;
        if (llmBudget.trim() !== initialLlmBudget) {
          const n = parseInt(llmBudget.trim(), 10);
          llmPatch.token_budget = llmBudget.trim() === '' || !(n > 0) ? null : n;
        }
        if (llmResolverCap.trim() !== initialLlmResolverCap) {
          const n = parseInt(llmResolverCap.trim(), 10);
          llmPatch.resolver_max_tokens = llmResolverCap.trim() === '' || !(n > 0) ? null : n;
        }
        if (clearLlmKey) llmPatch.api_key_plain = null;
        else if (llmApiKey.trim()) llmPatch.api_key_plain = llmApiKey.trim();
        if (Object.keys(llmPatch).length > 0) {
          // workflow-generic (found while adding the block below): the hub merges
          // config with jsonb `||`, which is SHALLOW — a nested object replaces the
          // stored one whole. Sending only the changed llm keys wiped the untouched
          // siblings (save a token budget → lose the model AND the encrypted key).
          // So the whole block rides: the stored values, overlaid with the patch.
          // The ciphertext is resent as-is when the key is unchanged; `api_key_plain`
          // is what the BFF encrypts, and it replaces the ciphertext when present.
          const storedLlm = { ...(initial?.config?.llm || {}) };
          if ('api_key_plain' in llmPatch) delete storedLlm.api_key_enc;
          body.config = {
            ...(body.config || {}),
            llm: { ...storedLlm, ...((body.config && body.config.llm) || {}), ...llmPatch },
          };
        }
        // workflow-generic: config.workflow — same whole-block rule as llm above.
        const wfPatch = {};
        if (wfGuidance !== initialWfGuidance) wfPatch.guidance = wfGuidance.trim() || null;
        if (wfExclude !== initialWfExclude) {
          const items = wfExclude.split(/[\n,]/).map((x) => x.trim()).filter(Boolean);
          wfPatch.exclude = items.length ? items : null;
        }
        if (wfTimeout.trim() !== initialWfTimeout) {
          const n = parseInt(wfTimeout.trim(), 10);
          wfPatch.dispatch_timeout_s = wfTimeout.trim() === '' || !(n > 0) ? null : n;
        }
        if (wfKeep.trim() !== initialWfKeep) {
          const n = parseInt(wfKeep.trim(), 10);
          wfPatch.keep_recent_turns = wfKeep.trim() === '' || !(n >= 0) ? null : n;
        }
        if (Object.keys(wfPatch).length > 0) {
          body.config = {
            ...(body.config || {}),
            workflow: { ...(initial?.config?.workflow || {}), ...wfPatch },
          };
        }
      }
      // output-second-screen: per-station output-panel config. Send a key only
      // when it changed vs. the loaded value; '' clears it (null on edit) /
      // omits it (create), letting the agent fall through to its default.
      const _obsTrim = outputObserversMax.trim();
      if (_obsTrim !== (initialObsMax == null ? '' : String(initialObsMax))) {
        body.config = {
          ...(body.config || {}),
          output_observers_max: _obsTrim === '' ? null : parseInt(_obsTrim, 10),
        };
      }
      const _ttlTrim = outputShareTtlHours.trim();
      if (_ttlTrim !== (initialShareTtl == null ? '' : String(initialShareTtl))) {
        body.config = {
          ...(body.config || {}),
          output_share_ttl_hours: _ttlTrim === '' ? null : Number(_ttlTrim),
        };
      }
      const _retTrim = fileRetentionDays.trim();
      if (_retTrim !== (initialRetention == null ? '' : String(initialRetention))) {
        body.config = {
          ...(body.config || {}),
          chat_file_retention_days: _retTrim === '' ? null : parseInt(_retTrim, 10),
        };
      }
      // output-share-public: only send when changed (boolean).
      if (outputSharePublic !== initialSharePublic) {
        body.config = { ...(body.config || {}), output_share_public: outputSharePublic };
      }
      // embeddable-card: only send when changed (boolean).
      if (embedPublic !== initialEmbedPublic) {
        body.config = { ...(body.config || {}), embed_public: embedPublic };
      }
      if (embedAct !== initialEmbedAct) {
        body.config = { ...(body.config || {}), embed_act: embedAct };
      }
      // presto-display-config: write the renamed key when changed (boolean).
      if (secondaryDisplay !== initialSecondaryDisplay) {
        body.config = { ...(body.config || {}), secondary_display: secondaryDisplay };
      }
      // presto-scheduled-view: what the secondary display shows (live | scheduled), when changed.
      if (displaySource !== initialDisplaySource) {
        body.config = { ...(body.config || {}), display_source: displaySource };
      }
      if (backup !== initialBackup) {
        body.config = { ...(body.config || {}), backup };
      }
      // station-settings-admin: helper-declared settings, per helper (the hub merges per set).
      if (editing && Object.keys(settingsEdits).length > 0) {
        body.config = { ...(body.config || {}), ...settingsPatch(settingsEdits, settingsSchema) };
      }
      // admin-node-station-edit slug: merge the Properties section's raw-key edits
      // (non-owned config keys; null clears a key via the hub's jsonb shallow
      // merge). Last so it can't clobber a section-owned key — PropertiesSection
      // already excludes owned keys, this is just ordering insurance.
      if (editing && propsPatch && Object.keys(propsPatch).length > 0) {
        body.config = { ...(body.config || {}), ...propsPatch };
      }
      // station-ownership-and-audit: chat-user owner (aveq-agent account id).
      // Edit-only; sent only when changed vs. the loaded value. '' clears it
      // (null). Data capture only — owner→admin rights deferred to sso-unification.
      if (editing && owner.trim() !== initialOwner) {
        const t = owner.trim();
        if (t === '') {
          body.owner_account_id = null;
        } else {
          const oid = parseInt(t, 10);
          if (Number.isNaN(oid) || oid < 0 || String(oid) !== t) {
            setError('Owner account id must be a whole number (or blank to clear).');
            setSubmitting(false);
            return;
          }
          body.owner_account_id = oid;
        }
      }
      // Route create/update through the node's network qbric so the post-save
      // reload-stations admin:exec reaches a peer-network node. The BFF reads
      // ?network in _qbric_base_for_request; without it the reload defaults to
      // the core qbric and never reaches a downstream node, so a newly-added
      // station's spec wouldn't auto-publish (station-picker-node-local-helpers).
      // station-description slug: also pass node_id on the PATCH so the BFF can
      // fire reload-stations without the credential-less hub station→node lookup
      // (which always returns None on this surface). Same pattern greeting uses;
      // without it a metadata-only edit (name/address/description) saved to the
      // DB but never re-broadcast, so it "didn't stick" until a qbric restart.
      const netQs = stationRouteQs(url);
      const r = await fetch(url + netQs, {
        method, headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const text = await r.text();
      let parsed = null;
      try { parsed = JSON.parse(text); } catch (_) {}
      if (!r.ok) {
        throw new Error((parsed && (parsed.message || parsed.error)) || text || `HTTP ${r.status}`);
      }
      // Owner-authored greeting (handmade-greetings slug). On Edit, only
      // PATCH if the operator changed the value — avoids a spurious clear
      // when they leave the field untouched. On Create, post the greeting
      // after the station exists. Empty after-strip → clears the key per
      // the SP's contract.
      const createdId = editing ? initial.id
        : (parsed && parsed.entity && parsed.entity.station && parsed.entity.station.station_id);
      const changedGreeting = greeting !== initialGreeting || greetingOff !== initialGreetingOff;
      if (createdId && changedGreeting) {
        // Pass the owning node_id so aveq-admin can fire admin:exec
        // reload-stations against the right node without a hub
        // station→node lookup (which uses a node-token-required endpoint
        // admin can't auth to). greeting-cache-invalidation slug.
        // node_id lets aveq-admin target the right node; network routes the
        // reload through that node's qbric (peer-network nodes are unreachable
        // via the default core qbric) — same fix as the create/update path.
        const params = [];
        if (nodeId) params.push(`node_id=${encodeURIComponent(nodeId)}`);
        if (networkCanonical) params.push(`network=${encodeURIComponent(networkCanonical)}`);
        const qs = params.length ? `?${params.join('&')}` : '';
        const gr = await fetch(`/api/stations/${createdId}/greeting${qs}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          // station-empty-greeting: greeting_off → hub stores '' (no greeting);
          // else send the text (blank clears the key → LLM auto-greeting).
          body: JSON.stringify({ greeting: greetingOff ? '' : greeting, greeting_off: greetingOff }),
        });
        if (!gr.ok) {
          const gtext = await gr.text();
          throw new Error(`greeting update failed: ${gtext}`);
        }
      }
      onSaved && onSaved();
      onClose();
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setSubmitting(false);
    }
  };

  const renderBasics = () => (
    <>
          <label style={labelStyle}>
            <span>Name</span>
            <input value={name} onChange={(e) => setName(e.target.value)}
                   autoFocus maxLength={128} required style={inputStyle} />
          </label>
          <label style={labelStyle}>
            <span>Address</span>
            <input value={address} onChange={(e) => setAddress(e.target.value)}
                   maxLength={256} placeholder="(optional)" style={inputStyle} />
          </label>
          {/* station-description: edit-only (the create SP takes no description).
              Shown under the station name on /s/<id>; capped at 160 chars. */}
          {editing && (
            <label style={labelStyle}>
              <span>Description <span style={{ color: '#888', fontWeight: 'normal' }}>({160 - description.length} left)</span></span>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)}
                        maxLength={160} rows={2} placeholder="(optional) short blurb shown under the station name"
                        style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
            </label>
          )}
          {!editing && (
            <div style={{ display: 'flex', gap: 8 }}>
              <label style={{ ...labelStyle, flex: 1 }}>
                <span>Latitude</span>
                <input value={lat} onChange={(e) => setLat(e.target.value)}
                       placeholder="(optional)" style={inputStyle} />
              </label>
              <label style={{ ...labelStyle, flex: 1 }}>
                <span>Longitude</span>
                <input value={lng} onChange={(e) => setLng(e.target.value)}
                       placeholder="(optional)" style={inputStyle} />
              </label>
            </div>
          )}
          {editing && (
            <label style={labelStyle}>
              <span>Owner (account id)</span>
              <input value={owner} onChange={(e) => setOwner(e.target.value)}
                     inputMode="numeric" placeholder="(unassigned)" style={inputStyle} />
              <span style={{ fontSize: '0.7rem', color: '#888', marginTop: 2 }}>
                aveq-agent account that owns this station — blank to clear. Owner
                admin rights arrive with sso-unification.
              </span>
            </label>
          )}
    </>
  );

  const renderConversation = () => (
    <>
          {renderConflicts()}
          {roomHelperError && <div style={conflictHardStyle}>⛔ {roomHelperError}</div>}
          {/* broadcasting-as-shared-session (D9): the conversation config is two
              orthogonal axes now — session TOPOLOGY (cardinality) and the
              RESPONDER (participant) — replacing the single resolver_mode
              dropdown. Both apply to new chat sessions; running sessions keep
              their current setup. */}
          {/* aveq-admin-ui-tweaks: Session type + Assistant are radio groups (the
              option set is small + fixed; radios show all choices at a glance). The
              transactional-model picker below stays a <select> (open-ended list). */}
          <div style={convSectionStyle}>
            <span>Session type <span style={{ color: '#888', fontWeight: 'normal' }}>
              (Private = a 1-1 session per visitor; Shared room = one room all
              visitors join — needs the station_chat_endpoints helper)
            </span></span>
            <div role="radiogroup" aria-label="Session type" style={radioGroupStyle}>
              {cardinalityOptions.map((opt) => (
                <label key={opt.value} style={radioRowStyle}>
                  <input type="radio" name="cardinality" value={opt.value}
                         checked={cardinality === opt.value
                                  || (!cardinality && opt.value === 'private')}
                         onChange={() => chooseCardinality(opt.value)} />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
          {/* Room notifications (config.web_push): members not in the room get posts in their
              Messages; any web push follows each person's own Settings. Shared rooms only. */}
          {cardinality === 'shared' && (
            <label style={helperRowStyle}>
              <input type="checkbox" checked={webPush}
                     onChange={(e) => setWebPush(e.target.checked)} />
              <span>Notify members who aren't in the room <span style={{ color: '#888', fontWeight: 'normal' }}>
                (posts go to their Messages; pushes follow each person's settings)
              </span></span>
            </label>
          )}
          <div style={convSectionStyle}>
            <span>Assistant <span style={{ color: '#888', fontWeight: 'normal' }}>
              (the responder; for a shared room this is what answers @aveq)
            </span></span>
            <div role="radiogroup" aria-label="Assistant" style={radioGroupStyle}>
              {participantOptions.map((opt) => (
                <label key={opt.value} style={radioRowStyle}>
                  <input type="radio" name="participant" value={opt.value}
                         checked={participant === opt.value
                                  || (!participant && opt.value === 'conversational')}
                         onChange={() => {
                           setParticipant(opt.value);
                           // Clear provider when switching away from transactional so
                           // the next save matches what the hub validator accepts.
                           if (opt.value !== 'transactional') {
                             setTransactionalProvider('');
                           } else if (!transactionalProvider) {
                             // Default-fill when picking transactional fresh.
                             setTransactionalProvider(
                               (transactionalProviders[0] && transactionalProviders[0].value)
                               || LEGACY_LITE_DEFAULT_PROVIDER);
                           }
                         }} />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
          {participant === 'transactional' && (
            <label style={convSectionStyle}>
              <span>Transactional model <span style={{ color: '#888', fontWeight: 'normal' }}>
                (provider + model the resolver dials)
              </span></span>
              <select value={transactionalProvider}
                      onChange={(e) => setTransactionalProvider(e.target.value)}
                      style={inputStyle}>
                {transactionalProviders.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </label>
          )}
    </>
  );

  const renderOutput = () => (
    <>
          {/* output-second-screen slug: per-station output (card) panel. Blank
              = station default (panel + 1 share). 0 observers = no output panel
              at all (chat-only, e.g. a StationChat room). */}
          <label style={labelStyle}>
            <span>Output panels <span style={{ color: '#888', fontWeight: 'normal' }}>
              (extra shareable second screens; 0 = no output panel; blank = default 1)
            </span></span>
            <input type="number" min="0" step="1" value={outputObserversMax}
                   onChange={(e) => setOutputObserversMax(e.target.value)}
                   placeholder="1" style={inputStyle} />
          </label>
          <label style={labelStyle}>
            <span>Share-link lifetime <span style={{ color: '#888', fontWeight: 'normal' }}>
              (hours a “Split output” link stays valid; blank = default 24)
            </span></span>
            <input type="number" min="1" step="1" value={outputShareTtlHours}
                   onChange={(e) => setOutputShareTtlHours(e.target.value)}
                   placeholder="24" style={inputStyle} />
          </label>
          {/* output-share-public slug: opt-in anonymous viewing. Security
              relaxation — the share link becomes a bearer credential. */}
          <label style={{ ...labelStyle, flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
            <input type="checkbox" checked={outputSharePublic}
                   onChange={(e) => setOutputSharePublic(e.target.checked)} />
            <span>Allow anonymous viewing <span style={{ color: '#888', fontWeight: 'normal' }}>
              (logged-out kiosk / wall display can open a “Split output” link without
              signing in — anyone with the time-bound link can view; off by default)
            </span></span>
          </label>
          {/* embeddable-card: opt-in to public embedding of the card on third-party
              pages. Security relaxation — the station's live card output becomes
              world-viewable via <iframe src=".../embed/s/<id>"> with no token/login. */}
          <label style={{ ...labelStyle, flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
            <input type="checkbox" checked={embedPublic}
                   onChange={(e) => setEmbedPublic(e.target.checked)} />
            <span>Publicly embeddable <span style={{ color: '#888', fontWeight: 'normal' }}>
              (allow this station's live card to be embedded read-only on any external
              website via an iframe, no sign-in — anyone can view; off by default)
            </span></span>
          </label>
          {/* embeddable-card-interactive: 2nd opt-in — allow anonymous ACTIONS from the
              embed (config.embed_act). Only meaningful with "Publicly embeddable"; a guest
              gets an ephemeral identity, still subject to the station's anonymous access gate. */}
          <label style={{ ...labelStyle, flexDirection: 'row', alignItems: 'center', gap: '8px',
                          marginLeft: '24px', opacity: embedPublic ? 1 : 0.5 }}>
            <input type="checkbox" checked={embedAct} disabled={!embedPublic}
                   onChange={(e) => setEmbedAct(e.target.checked)} />
            <span>↳ Allow embedded actions <span style={{ color: '#888', fontWeight: 'normal' }}>
              (let a publicly-embedded card send actions back to the station as an anonymous
              guest — e.g. play/interact; requires "Publicly embeddable"; off by default)
            </span></span>
          </label>
          {/* presto-display-config: opt this station into the secondary-display
              (split-output) second screen — `config.secondary_display`. */}
          <label style={{ ...labelStyle, flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
            <input type="checkbox" checked={secondaryDisplay}
                   onChange={(e) => setSecondaryDisplay(e.target.checked)} />
            <span>Secondary display for split output <span style={{ color: '#888', fontWeight: 'normal' }}>
              (show this station on a secondary display / second screen — e.g. a Presto;
              off by default)
            </span></span>
          </label>
          {/* presto-scheduled-view: what a secondary display shows — the live interactive output,
              or the station's latest scheduled-task cards. Only relevant once a secondary display
              is enabled above. */}
          {secondaryDisplay && (
            <label style={labelStyle}>
              <span>Display source</span>
              <select value={displaySource} onChange={(e) => setDisplaySource(e.target.value)}>
                <option value="live">Live output (interactive session)</option>
                <option value="scheduled">Scheduled output (latest scheduled-task cards)</option>
              </select>
              <span style={{ color: '#888', fontWeight: 'normal', fontSize: '0.85em' }}>
                “Scheduled” shows the most recent scheduled task pinned to this station — a wall
                display of scheduled results, no interactive session needed.
              </span>
            </label>
          )}
    </>
  );

  const renderBackup = () => (
    <>
          {/* node-data-backup: off by default. A station's durable files are only copied
              off-host when its owner asks — a sensor station's readings are noise, a chat
              room's history is not. */}
          <label style={{ ...labelStyle, flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
            <input type="checkbox" checked={backup}
                   onChange={(e) => setBackup(e.target.checked)} />
            <span>Back up this station's data <span style={{ color: '#888', fontWeight: 'normal' }}>
              (copy the files this station's helpers persist — e.g. a chat room's message
              history — to the off-host store on a daily schedule; off by default)
            </span></span>
          </label>
          <label style={labelStyle}>
            <span>Delete chat files after <span style={{ color: '#888', fontWeight: 'normal' }}>
              (days; files in this station's chat, whether posted by people or made by its
              helpers, and their previews, are removed once older than this; blank = keep forever)
            </span></span>
            <input type="number" min="1" step="1" value={fileRetentionDays}
                   onChange={(e) => setFileRetentionDays(e.target.value)}
                   placeholder="Keep forever" aria-label="Delete chat files after (days)"
                   style={inputStyle} />
          </label>
    </>
  );

  const renderAggregator = () => (
    <>
          {/* aggregator-station-llm-config slug: per-aggregator Claude model +
              API key (writes config.llm.{model, api_key_enc}). Only shown for
              aggregator stations. The key is write-only — the modal only knows
              whether one is set; entering a value replaces it, the checkbox
              clears it. The admin BFF encrypts before the hub. */}
          {isAggregatorStation && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: '0.85rem', marginBottom: 6 }}>
                Aggregator LLM <span style={{ color: '#888', fontWeight: 'normal' }}>
                  (this aggregator's Claude model + API key; leave blank to use server defaults)
                </span>
              </div>
              <label style={labelStyle}>
                <span>Model</span>
                <input type="text" value={llmModel}
                       onChange={(e) => setLlmModel(e.target.value)}
                       placeholder="e.g. claude-sonnet-4-6 (default)"
                       style={inputStyle} />
              </label>
              <label style={labelStyle}>
                <span>Token budget per run</span>
                <input type="number" min="1" value={llmBudget}
                       onChange={(e) => setLlmBudget(e.target.value)}
                       placeholder="server default (65000)"
                       style={inputStyle} />
              </label>
              <label style={labelStyle}>
                <span>Resolver output cap <span style={{ color: '#888', fontWeight: 'normal' }}>(tokens the chat resolver may spend emitting one call; raise for long free-text goals)</span></span>
                <input type="number" min="1" value={llmResolverCap}
                       onChange={(e) => setLlmResolverCap(e.target.value)}
                       placeholder="agent default (1024)"
                       style={inputStyle} />
              </label>
              <label style={labelStyle}>
                <span>API key {llmKeyIsSet && !clearLlmKey && (
                  <span style={{ color: '#888', fontWeight: 'normal' }}>
                    (a key is set — enter a new one to replace)
                  </span>
                )}</span>
                <input type="password" value={llmApiKey}
                       onChange={(e) => setLlmApiKey(e.target.value)}
                       placeholder={llmKeyIsSet ? '•••••••• (unchanged)' : 'sk-ant-… (stored encrypted)'}
                       disabled={clearLlmKey}
                       autoComplete="new-password"
                       style={inputStyle} />
              </label>
              {llmKeyIsSet && (
                <label style={helperRowStyle}>
                  <input type="checkbox" checked={clearLlmKey}
                         onChange={(e) => {
                           setClearLlmKey(e.target.checked);
                           if (e.target.checked) setLlmApiKey('');
                         }} />
                  <span>Clear stored API key (fall back to server default)</span>
                </label>
              )}
            </div>
          )}
    </>
  );

  // workflow-generic: the Workflow station's own settings (config.workflow).
  const renderWorkflow = () => (
    <>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: '0.85rem', marginBottom: 6 }}>
              Workflow orchestrator <span style={{ color: '#888', fontWeight: 'normal' }}>
                (read only by a station running the Workflow module; blank = server default)
              </span>
            </div>
            <label style={labelStyle}>
              <span>Guidance <span style={{ color: '#888', fontWeight: 'normal' }}>(house rules appended to the orchestrator's prompt — "prefer X over Y for…")</span></span>
              <textarea value={wfGuidance} onChange={(e) => setWfGuidance(e.target.value)}
                        maxLength={4000} rows={4}
                        placeholder="e.g. For anything about trains prefer TrainInfo over Directory."
                        style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }} />
            </label>
            <label style={labelStyle}>
              <span>Exclude <span style={{ color: '#888', fontWeight: 'normal' }}>(one per line: Set.function, or Set.* for a whole set — never offered as tools)</span></span>
              <textarea value={wfExclude} onChange={(e) => setWfExclude(e.target.value)}
                        rows={3} placeholder={'Tobin.play\nSensorFeed.*'}
                        style={{ ...inputStyle, fontFamily: 'monospace', resize: 'vertical' }} />
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <label style={{ ...labelStyle, flex: 1 }}>
                <span>Dispatch timeout (s)</span>
                <input type="number" min="1" value={wfTimeout}
                       onChange={(e) => setWfTimeout(e.target.value)}
                       placeholder="server default" style={inputStyle} />
              </label>
              <label style={{ ...labelStyle, flex: 1 }}>
                <span>Keep recent turns <span style={{ color: '#888', fontWeight: 'normal' }}>(0 = no trace compaction)</span></span>
                <input type="number" min="0" value={wfKeep}
                       onChange={(e) => setWfKeep(e.target.value)}
                       placeholder="server default (3)" style={inputStyle} />
              </label>
            </div>
          </div>
    </>
  );

  const renderHelpers = () => (
    <>
          {renderConflicts()}
          {roomHelperError && <div style={conflictHardStyle}>⛔ {roomHelperError}</div>}
          {/* Helpers fieldset (station-helper-gating slug). Hidden until
              the catalog + node policy load, so the modal doesn't flash
              an empty picker before the data arrives. */}
          {candidateModules !== null && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: '0.85rem', marginBottom: 6 }}>
                Helpers <span style={{ color: '#888', fontWeight: 'normal' }}>
                  (checked helpers are published on this station)
                </span>
              </div>
              {/* station-picker-bare-relay-node-local: gate on candidateModules
                  emptiness, NOT nodeIsBareRelay. node-local (extra/) helpers
                  bypass the include/exclude policy and ARE in candidateModules
                  even for a bare-relay node — short-circuiting on nodeIsBareRelay
                  hid them (e.g. a standalone bare-relay node with a custom extra/
                  helper couldn't attach it to a station). Only show the
                  no-helpers note when there's genuinely nothing to offer. */}
              {candidateModules.length === 0 ? (
                <div style={{ fontSize: '0.85rem', color: '#888' }}>
                  {nodeIsBareRelay
                    ? 'Bare relay node — no helpers to enable on this station.'
                    : 'No helpers loaded on this node.'}
                </div>
              ) : (
                <div style={fieldsetBoxStyle}>
                  {candidateModules.map((m) => (
                    <label key={m} style={helperRowStyle}>
                      <input type="checkbox"
                             checked={selectedHelpers.has(m)}
                             onChange={() => toggleHelper(m)} />
                      <span>{m}</span>
                      {nodeLocalHelpers.includes(m) && (
                        <span style={nodeLocalBadgeStyle} title="Loaded on the node (extra/), not in the provider catalog">node-local</span>
                      )}
                    </label>
                  ))}
                </div>
              )}
              {hasLegacyEnabledFlags && (
                <div style={{ marginTop: 6, fontSize: '0.75rem', color: '#888' }}>
                  This station carries legacy <code>*_enabled</code> flags; they'll be cleaned up on the next save.
                </div>
              )}
            </div>
          )}
    </>
  );

  const renderGreeting = () => (
    <>
          <label style={labelStyle}>
            <span>Greeting <span style={{ color: '#888', fontWeight: 'normal' }}>(shown verbatim to visitors)</span></span>
            <textarea value={greetingOff ? '' : greeting} onChange={(e) => setGreeting(e.target.value)}
                      maxLength={1024} rows={3} disabled={greetingOff}
                      placeholder={greetingOff ? 'No greeting — the station opens silent.' : "Leave blank to fall back to the helper's default."}
                      style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical', opacity: greetingOff ? 0.5 : 1 }} />
          </label>
          {/* station-empty-greeting: explicit opt-out of any greeting, distinct
              from a blank field (which falls back to the LLM auto-greeting). */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, fontWeight: 'normal', fontSize: '0.85rem' }}>
            <input type="checkbox" checked={greetingOff} onChange={(e) => setGreetingOff(e.target.checked)} />
            <span>No greeting <span style={{ color: '#888' }}>(skip the intro entirely; leaving the box above blank = LLM auto-greeting)</span></span>
          </label>
    </>
  );

  // MR !575 review #3: the Image pane's transient notices live in PARENT state,
  // but the pane itself unmounts when you switch category — so "Image saved."
  // was still sitting there on the next visit, describing an action from minutes
  // ago. Clear both on the way out. (Not on the way IN: a message set by the
  // action that just ran must survive the render that follows it.)
  useEffect(() => {
    if (activeCat !== 'image') { setImageMsg(null); setImageError(null); }
  }, [activeCat]);

  // station-avatar: downscale an uploaded image CLIENT-SIDE to a small square
  // avatar (center-crop → 128px → JPEG data URI) so it stays tiny in config and
  // needs no backend upload endpoint. Rejects anything still over ~30 KB.
  const onImageFile = (file) => {
    setImageError(null);
    if (!file) return;
    const reader = new FileReader();
    reader.onerror = () => setImageError('Could not read that file.');
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => setImageError('That file is not a readable image.');
      img.onload = () => {
        const SIZE = 128;
        const canvas = document.createElement('canvas');
        canvas.width = canvas.height = SIZE;
        const ctx = canvas.getContext('2d');
        const s = Math.min(img.width, img.height);          // center-crop to square
        ctx.drawImage(img, (img.width - s) / 2, (img.height - s) / 2, s, s, 0, 0, SIZE, SIZE);
        let uri = canvas.toDataURL('image/jpeg', 0.82);
        if (uri.length > 30 * 1024) uri = canvas.toDataURL('image/jpeg', 0.6);
        if (uri.length > 30 * 1024) {
          setImageError('Image is too detailed to shrink under 30 KB — try a simpler picture.');
          return;
        }
        setImageDataUri(uri);
        setImageMsg(null);
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  // station-avatar-save-undo: persist ONLY the picture, leaving every other
  // pane's pending edits untouched. Same endpoint + network routing the full
  // submit uses, with a one-key config body — the hub shallow-merges station
  // config jsonb, so this cannot clobber a sibling key. On success the saved
  // baseline moves, which disables Undo and drops image_url from a later
  // footer Save. onSaved() refreshes the station list behind the modal.
  const saveImage = async () => {
    if (!editing || !initial?.id) return;
    setImageSaving(true);
    setImageError(null);
    setImageMsg(null);
    try {
      const url = `/api/stations/${initial.id}`;
      const r = await fetch(url + stationRouteQs(url), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ config: { image_url: imageDataUri || null } }),
      });
      const text = await r.text();
      if (!r.ok) {
        let parsed = null;
        try { parsed = JSON.parse(text); } catch (_) {}
        throw new Error((parsed && (parsed.message || parsed.error)) || text || `HTTP ${r.status}`);
      }
      setSavedImage(imageDataUri);
      setImageMsg(imageDataUri ? 'Image saved.' : 'Image removed.');
      onSaved && onSaved();
    } catch (e) {
      setImageError(String(e.message || e));
    } finally {
      setImageSaving(false);
    }
  };

  // station-avatar-save-undo: revert is AUTHORITATIVE — it re-reads the station
  // from the server instead of trusting the baseline this modal opened with. The
  // local baseline goes stale the moment anyone else changes the picture (another
  // operator, another tab, a Properties edit elsewhere), and reverting to a stale
  // copy would quietly clobber the newer one on the next save. There is no plain
  // GET /api/stations/{id} on the BFF, so the node-detail payload — the same
  // response NodeDetail seeds this modal from — is the source.
  //
  // MR !575 review #1: that payload fails OPEN. The BFF overlays the hub's
  // authoritative station config onto qbric's AUTHENTICATE-time cached copy, but
  // when the hub read fails it silently serves the cached copy with a 200. So a
  // naive re-read could adopt a STALE picture and report success — the very bug
  // this button exists to prevent, one layer back. The BFF now stamps
  // `config_authoritative` on each station whose config genuinely came from the
  // hub, and we refuse to move the baseline without it.
  //
  // On any failure we surface an error and leave the picker untouched. Guessing
  // (falling back to the cached value, or worse, to blank) is what this whole
  // change exists to avoid.
  const revertImage = async () => {
    if (!editing || !initial?.id) return;
    setImageReverting(true);
    setImageError(null);
    setImageMsg(null);
    try {
      if (!nodeId) throw new Error('no node id on this form');
      // MR !575 review #4: this deliberately does NOT go through stationRouteQs.
      // That builder is for /api/stations/{id} writes and emits node_id, which is
      // meaningless on /api/nodes/{id} (the node IS the path) — it only exists
      // there so the BFF can fire reload-stations. Different endpoint, different
      // params; the two are not the drift the shared builder guards against.
      const qs = networkCanonical ? `?network=${encodeURIComponent(networkCanonical)}` : '';
      const r = await fetch(`/api/nodes/${encodeURIComponent(nodeId)}${qs}`,
                            { credentials: 'same-origin' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      const row = ((data && data.stations) || [])
        .find((st) => String(st.id) === String(initial.id));
      if (!row) throw new Error('the station is no longer on this node');
      // Fail closed: an unstamped row means the BFF served qbric's cached config
      // (hub read failed) and cannot vouch for it being current.
      if (row.config_authoritative !== true) {
        throw new Error('the server could not confirm the stored picture is current');
      }
      const stored = (row.config && row.config.image_url) || '';
      const movedUnderUs = stored !== savedImage;
      setImageDataUri(stored);
      setSavedImage(stored);
      // MR !576 review #3: back to the canonical picture on the next Generate.
      // Variant 0 is the only addressable image here (it's what the fleet
      // carries), and a counter that only ever climbs means an operator who
      // re-rolled and then reverted can never get back to it without closing the
      // modal — so "Generate one" would be lying.
      setImageGenVariant(0);
      setImageMsg(movedUnderUs
        ? 'Reverted — note the stored picture had changed since you opened this form.'
        : 'Reverted to the saved picture.');
    } catch (e) {
      setImageError(`Could not re-read the saved picture: ${String(e.message || e)}`);
    } finally {
      setImageReverting(false);
    }
  };

  // station-avatar-generate: ask the BFF for a painted brush avatar. Server-side
  // because the picture is the call-for-call output of Python's Mersenne Twister
  // — a browser-side port would paint a DIFFERENT image for the same station and
  // disagree with every already-seeded avatar (see tools/station_avatar.py).
  //
  // This only fills the picker. "Save image" commits it and "Revert to saved"
  // discards it, so generation adds no new persistence path.
  const generateImage = async () => {
    if (!editing || !initial?.id) return;
    setImageGenerating(true);
    setImageError(null);
    setImageMsg(null);
    try {
      const r = await fetch(
        `/api/stations/${encodeURIComponent(initial.id)}/generated-avatar`
        + `?variant=${encodeURIComponent(imageGenVariant)}`,
        { credentials: 'same-origin' });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
      if (!data.image_url) throw new Error('no image came back');
      // MR !576 review #4: the 30 KB cap was a property of the UPLOAD path only
      // (onImageFile), so nothing bounded a generated URI. These run ~4.7 KB at
      // the worst measured, but make the cap belong to the picker rather than to
      // one input path — otherwise the invariant lives only in a unit test.
      if (data.image_url.length > 30 * 1024) {
        throw new Error('the generated picture came back too large (over 30 KB)');
      }
      setImageDataUri(data.image_url);
      // Review #6: trust the SERVER's variant, so the two stay in agreement if it
      // ever clamps, rather than incrementing a local counter in parallel.
      setImageGenVariant((typeof data.variant === 'number' ? data.variant : imageGenVariant) + 1);
      setImageMsg(imageGenVariant === 0
        ? 'Generated. Not saved yet — Save image to keep it.'
        : 'Generated another. Not saved yet — Save image to keep it.');
    } catch (e) {
      setImageError(`Could not generate a picture: ${String(e.message || e)}`);
    } finally {
      setImageGenerating(false);
    }
  };

  const imageDirty = imageDataUri !== savedImage;
  const imageBusy = imageSaving || imageReverting || imageGenerating;

  const renderImage = () => (
    <div style={{ ...labelStyle, gap: 10 }}>
      <div style={{ fontSize: '0.82rem', color: '#666' }}>
        A small picture shown as a circle in the station's nav. Uploads are cropped
        to a square and shrunk to a small avatar automatically — or generate a
        painted one, the same kind the stations were seeded with.
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{ width: 72, height: 72, borderRadius: '50%', overflow: 'hidden',
                      border: '1px solid #ddd', background: '#f6f8fa', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {imageDataUri
            ? <img src={imageDataUri} alt="Station" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <span style={{ fontSize: '0.7rem', color: '#aaa' }}>no image</span>}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ ...inputStyle, cursor: 'pointer', textAlign: 'center', background: '#f6f8fa' }}>
            {imageDataUri ? 'Replace image…' : 'Upload image…'}
            <input type="file" accept="image/*" style={{ display: 'none' }}
                   onChange={(e) => onImageFile(e.target.files && e.target.files[0])} />
          </label>
          {/* station-avatar-generate: a painted brush avatar for operators who
              don't have a picture to hand. First click gives the station's
              canonical one (what the fleet seeding produced); each further click
              re-rolls, since one deterministic image per station would make a
              second click look like a no-op. */}
          <button type="button" onClick={generateImage} disabled={imageBusy}
                  style={{ ...inputStyle, cursor: imageBusy ? 'default' : 'pointer',
                           background: '#f6f8fa', opacity: imageBusy ? 0.55 : 1 }}>
            {imageGenerating ? 'Generating…'
              : (imageGenVariant === 0 ? 'Generate one' : 'Generate another')}
          </button>
          {imageDataUri && (
            <button type="button"
                    onClick={() => { setImageDataUri(''); setImageError(null); setImageMsg(null);
                                     setImageGenVariant(0); }}
                    style={{ ...inputStyle, cursor: 'pointer', color: '#922', background: '#fff' }}>
              Remove
            </button>
          )}
        </div>
      </div>
      {/* station-avatar-save-undo: the picture commits (or reverts) on its own,
          so an operator can swap an avatar without saving the whole form. Both
          are inert until the picture actually differs from what's stored.
          "Revert to saved" (not "Undo") because it discards the pending edit
          against the SERVER's current picture — it never unwinds a save. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button type="button" onClick={saveImage} disabled={!imageDirty || imageBusy}
                style={{ ...inputStyle, cursor: (!imageDirty || imageBusy) ? 'default' : 'pointer',
                         background: '#f6f8fa', opacity: (!imageDirty || imageBusy) ? 0.55 : 1 }}>
          {imageSaving ? 'Saving…' : 'Save image'}
        </button>
        {/* MR !575 review #2: NOT gated on imageDirty. Revert is the only control
            that pulls in a picture changed elsewhere, and gating it on a local
            edit made it unavailable in exactly the case it's for. With a clean
            pane it reads as "refresh from the server" — the message below already
            distinguishes moved-from-stored vs. unchanged. */}
        <button type="button" onClick={revertImage} disabled={imageBusy}
                style={{ ...inputStyle, cursor: imageBusy ? 'default' : 'pointer',
                         background: '#fff', opacity: imageBusy ? 0.55 : 1 }}>
          {imageReverting ? 'Reverting…' : 'Revert to saved'}
        </button>
        <span style={{ fontSize: '0.75rem', color: '#888' }}>
          {imageDirty
            ? 'Unsaved change — Save image applies just the picture; Revert re-reads the stored one.'
            : 'Picture matches what\u2019s saved.'}
        </span>
      </div>
      {imageError && <div style={conflictHardStyle}>{imageError}</div>}
      {imageMsg && <div style={{ fontSize: '0.8rem', color: '#363' }}>{imageMsg}</div>}
    </div>
  );

  // admin-sectioned-dialogs: categories drive the two-pane layout. Only
  // `basics` carries a required field (name); the rest are optional, and
  // `aggregator` shows only for aggregator stations. No inter-category locking
  // here — no Station field depends on another category (Add Node exercises that).
  const categories = [
    { id: 'basics', label: 'Basics', description: 'Name, address, and (for new stations) coordinates. Name is required.', hasRequired: true, complete: name.trim() !== '', render: renderBasics },
    { id: 'conversation', label: 'Conversation', description: 'How the assistant responds. Applies to new chat sessions.', error: hardConflicts.length > 0 || !!roomHelperError, render: renderConversation },
    { id: 'output', label: 'Output', description: 'The card output panel and its shareable second-screen links.', render: renderOutput },
    { id: 'backup', label: 'Backup', description: "Whether this station's data is copied off-host, and how long chat files are kept.", render: renderBackup },
    { id: 'aggregator', label: 'Aggregator LLM', description: 'Claude model and API key for this aggregator station.', applicable: isAggregatorStation, render: renderAggregator },
    // workflow-generic: only meaningful on a station running the Workflow module,
    // but the form cannot tell which aggregator module a station runs beyond its
    // name — so it shows for every aggregator station and says so.
    { id: 'workflow', label: 'Workflow', description: 'House rules, exclusions and timeouts for the Workflow orchestrator on this station.', applicable: isAggregatorStation, render: renderWorkflow },
    { id: 'helpers', label: 'Helpers', description: 'Which of the node helpers this station can use.', render: renderHelpers },
    { id: 'greeting', label: 'Greeting', description: 'Optional opening message shown verbatim to visitors.', render: renderGreeting },
    // station-avatar: the station picture (only meaningful on an existing station,
    // since it saves into config via PATCH).
    { id: 'image', label: 'Image', description: "The station's picture, shown as a circle in the nav.",
      applicable: editing, render: renderImage },
    // admin-node-station-edit slug: Access + Properties only apply to an existing
    // station (a new one has no id / config / access rows yet).
    { id: 'access', label: 'Access', description: 'Protect / unprotect chat-user access to this station, and choose who may use its restricted function sets.',
      applicable: editing,
      render: () => (
        <>
          <AccessSection level="station" id={initial?.id} name={initial?.name} onChanged={onAccessChanged} />
          <FunctionSetAccess stationId={initial?.id} />
        </>
      ) },
    // admin-display-provisioning: provision/rotate/revoke the read key a screen uses
    // to fetch this station's frames (delivered via config — no device re-flash).
    // lite-node-kind: only for stations whose device announced `kind: "display"`
    // (not every editable station, not sensor Picos).
    { id: 'display', label: 'Display', description: "The read key a screen uses to fetch this station's rendered frames.",
      applicable: editing && stationKind === 'display',
      render: () => <DisplayCredentialSection stationId={initial?.id} config={initial?.config || {}} /> },
    // Helper-declared settings first, then the raw keys nothing else owns.
    { id: 'settings', label: 'Settings', description: "Settings the station's helpers declare, then the config keys no other section owns.",
      applicable: editing,
      render: () => (
        <>
          <SettingsSection schema={settingsSchema} edits={settingsEdits}
                           onEditsChange={setSettingsEdits} />
          <div style={subheadStyle}>Properties</div>
          <PropertiesSection properties={initial?.config || {}} ownedKeys={propertiesOwnedKeys} criticalKeys={NO_CRITICAL_KEYS} maskedKeys={MASKED_CONFIG_KEYS} onPatchChange={handlePropsPatch} />
        </>
      ) },
  ];

  return (
    <SectionedDialog
      title={editing
        ? <code style={titleNameStyle}>{initial.name || `station #${initial.id}`}</code>
        : 'Add station'}
      titleText={editing ? (initial.name || `station #${initial.id}`) : 'Add station'}
      categories={categories}
      activeId={activeCat}
      onSelect={setActiveCat}
      primaryLabel={editing ? 'Save' : 'Create'}
      submittingLabel="Saving…"
      submitting={submitting}
      onPrimary={submit}
      onCancel={onClose}
      error={error}
    />
  );
}

const subheadStyle = {
  fontSize: '0.85rem', fontWeight: 600, margin: '20px 0 8px',
  paddingTop: 12, borderTop: '1px solid #eee',
};
const labelStyle = {
  display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12,
  fontSize: '0.85rem',
};
// Title name styled like Add Node's "Add child of <name>" — a muted monospace
// <code> at 0.85em — consistent with Node Edit (admin-node-station-edit).
const titleNameStyle = {
  fontSize: '0.85em', color: '#555',
};
// aveq-admin-ui-tweaks: extra breathing room between the Conversation sub-sections
// (Session type / Assistant / Transactional model).
const convSectionStyle = { ...labelStyle, marginBottom: 22 };
// aveq-admin-ui-tweaks: radio groups for Session type + Assistant.
const radioGroupStyle = {
  display: 'flex', flexDirection: 'column', gap: 4, marginTop: 2,
};
const radioRowStyle = {
  display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.9rem',
  fontWeight: 'normal', cursor: 'pointer',
};
// helper-requirements slug: conflict banners — hard (blocks Save) vs soft (warn).
const conflictHardStyle = {
  fontSize: '0.8rem', color: '#922', background: '#fdecea',
  border: '1px solid #f5c6cb', borderRadius: 4, padding: '6px 8px', marginBottom: 6,
};
const conflictSoftStyle = {
  fontSize: '0.8rem', color: '#7a5b00', background: '#fff8e1',
  border: '1px solid #ffe08a', borderRadius: 4, padding: '6px 8px', marginBottom: 6,
};
const inputStyle = {
  padding: '6px 8px', fontSize: '0.9rem', border: '1px solid #ccc', borderRadius: 4,
};
const fieldsetBoxStyle = {
  display: 'flex', flexDirection: 'column', gap: 4,
  border: '1px solid #ddd', borderRadius: 4, padding: 8,
  maxHeight: 200, overflowY: 'auto',
};
const helperRowStyle = {
  display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem',
  cursor: 'pointer',
};

// station-picker-node-local-helpers: tag for helpers loaded on the node (extra/)
// that aren't in the operator's provider catalog.
const nodeLocalBadgeStyle = {
  fontSize: '0.65rem', fontWeight: 600, color: '#7a5', border: '1px solid #cda',
  borderRadius: 3, padding: '0 4px', marginLeft: 4, textTransform: 'uppercase',
  letterSpacing: '0.03em',
};
